/**
 * TaskBoard（タスクシート翻訳共有アプリ、姉妹プロジェクト）を地図の上に
 * 埋め込む（docs/08 §193）。
 *
 * iPhoneには他のアプリの上に浮かぶ「バブル」が無いため、GV4Dの中で
 * 地図とタスクシートを同時に見られるようにする。表示の形は3つ:
 * - シート: 下からせり上がる。上端をつまんで高さを変え、3段（少し・半分・
 *   ほぼ全部）に止まる。下まで下げるとバブルになる
 * - 小窓: 地図の上に浮かぶ窓。見出しをつまんで動かし、右下で大きさを変える
 * - バブル: 畳んだ状態。画面の端に丸いボタンが残り、タップで元の形に戻る。
 *   ドラッグで位置を変えられ、左右どちらかの端に寄る
 *
 * 中身は同じサーバーの /taskboard/（scripts/sync-taskboard.mjs で取り込んだ
 * TaskBoard そのもの）を iframe で表示する。取り込まれていないサーバーでは
 * TaskBoard の公開サイトを直接表示する。iframe はDOM上で動かすと読み込み
 * 直しになるため、形が変わってもDOMは動かさずクラスだけ切り替える。
 */

const LOCAL_URL = '/taskboard/index.html';
const LOCAL_MARKER = '/taskboard/taskboard-source.json';
const PUBLIC_URL = 'https://ngr2000.github.io/TaskBoard/';

type Mode = 'sheet' | 'window';

interface SavedLayout {
  mode: Mode;
  /** シートの高さ（画面の高さに対する割合） */
  sheetRatio: number;
  win: { left: number; top: number; width: number; height: number } | null;
  bubble: { side: 'left' | 'right'; top: number } | null;
  /** 同じサーバーにTaskBoardがあると確認できたか（オフラインで開くとき用） */
  localAvailable: boolean;
}

const LAYOUT_KEY = 'gv4d-taskboard-layout';
/** シートの止まる高さ（画面の高さに対する割合）。少し・半分・ほぼ全部 */
export const SHEET_STOPS = [0.32, 0.55, 0.9];
/** これより低く下げて離すとバブルにする */
const SHEET_COLLAPSE_RATIO = 0.18;

/** 離した高さに一番近い止まり位置。低すぎればnull（＝バブルにする） */
export function snapSheetRatio(ratio: number): number | null {
  if (ratio < SHEET_COLLAPSE_RATIO) return null;
  return SHEET_STOPS.reduce((best, s) => (Math.abs(s - ratio) < Math.abs(best - ratio) ? s : best));
}

/** 小窓・バブルを画面内に収める */
export function clampRect(
  r: { left: number; top: number; width: number; height: number },
  vw: number,
  vh: number,
  min = { width: 220, height: 180 },
): { left: number; top: number; width: number; height: number } {
  const width = Math.min(Math.max(r.width, min.width), vw);
  const height = Math.min(Math.max(r.height, min.height), vh);
  return {
    width,
    height,
    left: Math.min(Math.max(r.left, 0), vw - width),
    top: Math.min(Math.max(r.top, 0), vh - height),
  };
}

function loadLayout(): SavedLayout {
  const base: SavedLayout = { mode: 'sheet', sheetRatio: SHEET_STOPS[1], win: null, bubble: null, localAvailable: false };
  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    return raw ? { ...base, ...(JSON.parse(raw) as Partial<SavedLayout>) } : base;
  } catch {
    return base;
  }
}

function saveLayout(layout: SavedLayout): void {
  try {
    localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout));
  } catch {
    // 保存できなくても今回は効く
  }
}

/** TaskBoardが端末に残している「今見ているフライト」の名前（同じサーバーなので読める） */
function activeFlightLabel(): string {
  try {
    const key = localStorage.getItem('tb.activeFlight') ?? '';
    const flights = JSON.parse(localStorage.getItem('tb.flights.index') ?? '[]') as Array<{ key: string; label: string }>;
    return flights.find((f) => f.key === key)?.label ?? '';
  } catch {
    return '';
  }
}

/**
 * TaskBoard側でターゲットの座標がタップされたときに届く知らせ（TaskBoardの
 * app.js が postMessage で送る、docs/08 §194）
 */
export interface TaskBoardTargetMessage {
  coordinates: string;
  name: string;
  mma: string;
  task: string;
  color: string;
}

/** 届いた知らせを確かめて取り出す。形が違えば null（他から来たメッセージは無視する） */
export function parseTaskBoardTargetMessage(data: unknown): TaskBoardTargetMessage | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (d.source !== 'taskboard' || d.type !== 'show-target') return null;
  if (typeof d.coordinates !== 'string' || !d.coordinates.trim() || d.coordinates.length > 40) return null;
  const str = (v: unknown, max: number) => (typeof v === 'string' ? v.slice(0, max) : '');
  const color = str(d.color, 9);
  return {
    coordinates: d.coordinates.trim(),
    name: str(d.name, 60),
    mma: str(d.mma, 20),
    task: str(d.task, 40),
    // 地図の塗り色に使うので #rgb〜#rrggbbaa だけを通す
    color: /^#[0-9a-f]{3,8}$/i.test(color) ? color : '',
  };
}

/** 押した位置から少しでも動いたらドラッグとみなす（タップと区別する）距離 */
const DRAG_THRESHOLD_PX = 6;

/**
 * つまんで動かす操作。iframeの上をポインターが通るとイベントがiframeに
 * 取られるので、ドラッグ中はiframeへのポインター入力を止める。
 *
 * ★ 「離した」が届かないことがある（アプリの窓の外で離した・別の窓へ
 *   切り替わった等でポインターの捕捉が外れる）。以前はそのときドラッグが
 *   終わらないまま残り、小窓の大きさが変えられなくなった（最小の大きさで
 *   固まる。iframeも押せないまま）。PC版で実際に起き、アプリを開き直すまで
 *   直らなかった（利用者の指摘、2026-10-11、docs/08 §199）。捕捉が外れたとき
 *   （lostpointercapture）もドラッグを終わらせ、次に押したときに前のドラッグが
 *   残っていれば先に終わらせる
 * ★ 画面の文字が選択されていると、つまんで動かしたときにブラウザが「選択した
 *   文字のドラッグ」を始め、pointercancel を座標(0,0)で送ってくる。以前はこの
 *   座標をそのまま使ったため小窓が最小の大きさに縮み、選択が残る限り毎回
 *   そうなった（＝固まって見えた、実測で再現）。押したときに選択を外して
 *   既定の動作を止め、pointercancel の座標は使わない
 */
export function onDrag(
  handle: HTMLElement,
  /** ドラッグ中にポインター入力を止めるiframe（無ければ null） */
  frame: HTMLIFrameElement | null,
  cb: {
    start?: () => void;
    move: (dx: number, dy: number) => void;
    end: (moved: boolean, dx: number, dy: number) => void;
  },
): void {
  /** 終わっていないドラッグを終わらせる（無ければ null） */
  let finish: (() => void) | null = null;
  handle.addEventListener('pointerdown', (e) => {
    if ((e.target as HTMLElement).closest('button:not(.tb-bubble)')) return; // 見出しのボタンは押せるように
    if (e.button !== 0) return;
    finish?.();
    // 文字の選択・ブラウザの「選択した文字のドラッグ」を始めさせない。始まると
    // ブラウザがドラッグを横取りして pointercancel（座標0,0）を送ってくる
    e.preventDefault();
    window.getSelection()?.removeAllRanges();
    const x0 = e.clientX;
    const y0 = e.clientY;
    let last = { x: x0, y: y0 };
    let moved = false;
    try {
      handle.setPointerCapture(e.pointerId);
    } catch {
      // 捕捉できなくても、離したとき（pointerup）で終わる
    }
    if (frame) frame.style.pointerEvents = 'none';
    cb.start?.();
    const move = (ev: PointerEvent) => {
      last = { x: ev.clientX, y: ev.clientY };
      const dx = ev.clientX - x0;
      const dy = ev.clientY - y0;
      if (!moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      moved = true;
      cb.move(dx, dy);
    };
    const done = () => {
      if (finish !== done) return; // もう終わっている（離した後に捕捉が外れた知らせも来る）
      finish = null;
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
      handle.removeEventListener('lostpointercapture', lost);
      if (frame) frame.style.pointerEvents = '';
      cb.end(moved, last.x - x0, last.y - y0);
    };
    const up = (ev: PointerEvent) => {
      // pointercancel の座標は当てにならない（0,0で来る）。最後に分かっている位置で終える
      if (ev.type === 'pointerup') last = { x: ev.clientX, y: ev.clientY };
      done();
    };
    // 捕捉が外れたら、最後に分かっている位置で終わらせる
    const lost = () => done();
    finish = done;
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
    handle.addEventListener('lostpointercapture', lost);
  });
}

export function setupTaskboardPanel(
  options: {
    /**
     * タスクシートでターゲットの座標がタップされたとき（地図をそこへ動かす）。
     * `coverBottomPx` は下からのシートが画面の下を覆っている高さ（小窓なら0）。
     * 地図の中心をシートの上に見えている部分へ寄せるのに使う
     */
    onShowTarget?: (target: TaskBoardTargetMessage, coverBottomPx: number) => void;
  } = {},
): { toggle: () => void } {
  const host = document.getElementById('tb-host') as HTMLDivElement;
  const head = document.getElementById('tb-head') as HTMLDivElement;
  const frame = document.getElementById('tb-frame') as HTMLIFrameElement;
  const modeBtn = document.getElementById('tb-mode') as HTMLButtonElement;
  const minimizeBtn = document.getElementById('tb-minimize') as HTMLButtonElement;
  const closeBtn = document.getElementById('tb-close') as HTMLButtonElement;
  const resize = document.getElementById('tb-resize') as HTMLDivElement;
  const bubble = document.getElementById('tb-bubble') as HTMLButtonElement;
  const bubbleLabel = document.getElementById('tb-bubble-label') as HTMLSpanElement;

  const layout = loadLayout();
  let loaded = false;

  const vw = () => window.innerWidth;
  const vh = () => window.innerHeight;

  /** 中身を最初に開くときだけ読み込む（使わない人の通信・電池を使わない） */
  const ensureLoaded = async (): Promise<void> => {
    if (loaded) return;
    loaded = true;
    let local = false;
    try {
      const res = await fetch(LOCAL_MARKER, { cache: 'no-store' });
      local = res.ok && (res.headers.get('content-type') ?? '').includes('json');
      if (local) await res.json();
    } catch {
      // オフライン等。以前に確認できていれば同じサーバーのもの（TaskBoardのService Workerが持っている）を使う
      local = layout.localAvailable;
    }
    layout.localAvailable = local;
    saveLayout(layout);
    frame.src = local ? LOCAL_URL : PUBLIC_URL;
  };

  const applySheet = () => {
    host.style.removeProperty('left');
    host.style.removeProperty('top');
    host.style.removeProperty('width');
    host.style.setProperty('height', `${Math.round(layout.sheetRatio * 100)}dvh`);
  };

  const defaultWindow = () => {
    const width = Math.min(380, Math.round(vw() * 0.86));
    const height = Math.min(460, Math.round(vh() * 0.5));
    return { left: vw() - width - 12, top: Math.round(vh() * 0.18), width, height };
  };

  const applyWindow = () => {
    const r = clampRect(layout.win ?? defaultWindow(), vw(), vh());
    layout.win = r;
    host.style.left = `${r.left}px`;
    host.style.top = `${r.top}px`;
    host.style.width = `${r.width}px`;
    host.style.height = `${r.height}px`;
  };

  const applyBubble = () => {
    // 初期位置は左端（右端は地図のボタン列と重なる）
    const pos = layout.bubble ?? { side: 'left' as const, top: Math.round(vh() * 0.45) };
    const top = Math.min(Math.max(pos.top, 60), vh() - 80);
    bubble.style.top = `${top}px`;
    bubble.dataset.side = pos.side;
    bubble.style.removeProperty('left');
    const label = activeFlightLabel();
    bubbleLabel.textContent = label;
    bubble.title = label ? `タスクシート（${label}）` : 'タスクシート';
  };

  const show = (mode: Mode) => {
    layout.mode = mode;
    saveLayout(layout);
    host.dataset.mode = mode;
    modeBtn.textContent = mode === 'sheet' ? '小窓' : 'シート';
    modeBtn.setAttribute('aria-label', mode === 'sheet' ? '小窓にする' : '下からのシートにする');
    if (mode === 'sheet') applySheet();
    else applyWindow();
    host.hidden = false;
    bubble.hidden = true;
    void ensureLoaded();
  };

  /** 畳んでバブルにする（中身はそのまま残るので、戻すと続きから見られる） */
  const minimize = () => {
    host.hidden = true;
    applyBubble();
    bubble.hidden = false;
  };

  const close = () => {
    host.hidden = true;
    bubble.hidden = true;
  };

  modeBtn.onclick = () => show(layout.mode === 'sheet' ? 'window' : 'sheet');
  minimizeBtn.onclick = minimize;
  closeBtn.onclick = close;

  // シートは上端を、小窓は見出しをつまんで動かす
  let startHeight = 0;
  let startRect = { left: 0, top: 0, width: 0, height: 0 };
  onDrag(head, frame, {
    start: () => {
      startHeight = host.getBoundingClientRect().height;
      startRect = layout.win ?? defaultWindow();
      host.classList.add('dragging');
    },
    move: (dx, dy) => {
      if (layout.mode === 'sheet') {
        host.style.height = `${Math.max(0, startHeight - dy)}px`;
      } else {
        const r = clampRect({ ...startRect, left: startRect.left + dx, top: startRect.top + dy }, vw(), vh());
        host.style.left = `${r.left}px`;
        host.style.top = `${r.top}px`;
      }
    },
    end: (moved, dx, dy) => {
      host.classList.remove('dragging');
      if (!moved) return;
      if (layout.mode === 'sheet') {
        const snapped = snapSheetRatio((startHeight - dy) / vh());
        if (snapped === null) {
          minimize();
          return;
        }
        layout.sheetRatio = snapped;
        applySheet();
      } else {
        layout.win = clampRect({ ...startRect, left: startRect.left + dx, top: startRect.top + dy }, vw(), vh());
        applyWindow();
      }
      saveLayout(layout);
    },
  });

  // 小窓の右下で大きさを変える
  onDrag(resize, frame, {
    start: () => {
      startRect = layout.win ?? defaultWindow();
    },
    move: (dx, dy) => {
      const r = clampRect({ ...startRect, width: startRect.width + dx, height: startRect.height + dy }, vw(), vh());
      host.style.width = `${r.width}px`;
      host.style.height = `${r.height}px`;
    },
    end: (_moved, dx, dy) => {
      layout.win = clampRect({ ...startRect, width: startRect.width + dx, height: startRect.height + dy }, vw(), vh());
      applyWindow();
      saveLayout(layout);
    },
  });

  // バブル: タップで元の形に戻す。ドラッグで動かし、離したら近い方の端に寄せる
  let bubbleTop0 = 0;
  onDrag(bubble, frame, {
    start: () => {
      bubbleTop0 = bubble.getBoundingClientRect().top;
    },
    move: (dx, dy) => {
      const r = bubble.getBoundingClientRect();
      const x = (bubble.dataset.side === 'left' ? 8 : vw() - r.width - 8) + dx;
      bubble.style.left = `${Math.min(Math.max(x, 0), vw() - r.width)}px`;
      bubble.dataset.side = 'free';
      bubble.style.top = `${Math.min(Math.max(bubbleTop0 + dy, 0), vh() - r.height)}px`;
    },
    end: (moved) => {
      if (!moved) {
        show(layout.mode);
        return;
      }
      const r = bubble.getBoundingClientRect();
      layout.bubble = { side: r.left + r.width / 2 < vw() / 2 ? 'left' : 'right', top: Math.round(r.top) };
      saveLayout(layout);
      applyBubble();
    },
  });

  // キーボード（Enter・Space）で押されたときもタップと同じに。ポインター操作は上で扱う（detail>0）
  bubble.addEventListener('click', (e) => {
    if (e.detail === 0) show(layout.mode);
  });

  // TaskBoard（iframeの中）から、ターゲットの座標がタップされた知らせを受け取る。
  // 埋め込んだ iframe から・同じサーバーか TaskBoard の公開サイトから来たものだけを信じる
  const publicOrigin = new URL(PUBLIC_URL).origin;
  window.addEventListener('message', (e) => {
    if (e.source !== frame.contentWindow) return;
    if (e.origin !== location.origin && e.origin !== publicOrigin) return;
    const target = parseTaskBoardTargetMessage(e.data);
    if (!target) return;
    // ほぼ全部まで上げたシートだと地図が見えないので、半分まで下げる
    if (layout.mode === 'sheet' && layout.sheetRatio > SHEET_STOPS[1]) {
      layout.sheetRatio = SHEET_STOPS[1];
      applySheet();
      saveLayout(layout);
    }
    // シートの高さは動きの途中で読むとずれるので、止まり位置から計算する
    const cover = layout.mode === 'sheet' ? Math.round(layout.sheetRatio * vh()) : 0;
    options.onShowTarget?.(target, cover);
  });

  // 画面の向き・大きさが変わったら小窓とバブルを画面内に収め直す
  window.addEventListener('resize', () => {
    if (!host.hidden && layout.mode === 'window') applyWindow();
    if (!bubble.hidden) applyBubble();
  });
  // TaskBoard側でフライトを切り替えたらバブルの名前も変える（同じサーバーなので通知が届く）
  window.addEventListener('storage', (e) => {
    if (!bubble.hidden && (e.key === 'tb.activeFlight' || e.key === 'tb.flights.index')) applyBubble();
  });

  return {
    /** 右のボタン: 閉じていれば開く、開いていればバブルに畳む */
    toggle: () => {
      if (host.hidden) show(layout.mode);
      else minimize();
    },
  };
}
